import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error';

export type TagColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink'
  | 'gray';

type TagEntityType = 'issue' | 'workflow' | 'chaos_run' | 'card' | 'prompt';

export interface Tag {
  id: string;
  orgId: string;
  name: string;
  color: TagColor;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

export interface TagWithStats extends Tag {
  assignmentCount: number;
}

type TagListResponse = {
  ok: boolean;
  data: Tag[];
};

type TagStatsListResponse = {
  ok: boolean;
  data: TagWithStats[];
};

type TagResponse = {
  ok: boolean;
  tag: Tag;
};

/**
 * Fetch all tags for the current org
 */
export function useTagsQuery(options?: { withStats?: boolean }) {
  return useQuery<TagListResponse | TagStatsListResponse>({
    queryKey: ['tags', options?.withStats ? 'stats' : 'list'],
    queryFn: async () => {
      const endpoint = options?.withStats ? '/org/tags/stats' : '/org/tags';
      const res = await fetch(`${API_URL}${endpoint}`, { credentials: 'include' });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to load tags');
      }
      return res.json();
    },
  });
}

type CreateTagInput = {
  name: string;
  color: TagColor;
  description?: string;
};

/**
 * Create a new tag
 */
export function useCreateTagMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTagInput) => {
      const res = await fetch(`${API_URL}/org/tags`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to create tag');
      }
      return (await res.json()) as TagResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

type UpdateTagInput = {
  id: string;
  name?: string;
  color?: TagColor;
  description?: string | null;
};

/**
 * Update an existing tag
 */
export function useUpdateTagMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateTagInput) => {
      const res = await fetch(`${API_URL}/org/tags/${id}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to update tag');
      }
      return (await res.json()) as TagResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

/**
 * Delete a tag
 */
export function useDeleteTagMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tagId: string) => {
      const res = await fetch(`${API_URL}/org/tags/${tagId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to delete tag');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

/**
 * Fetch tags assigned to a specific entity
 */
export function useEntityTagsQuery(entityType: TagEntityType, entityId: string | undefined) {
  return useQuery<TagListResponse>({
    queryKey: ['entity-tags', entityType, entityId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/org/tags/entity/${entityType}/${entityId}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to load entity tags');
      }
      return res.json();
    },
    enabled: Boolean(entityId),
  });
}

type SetEntityTagsInput = {
  entityType: TagEntityType;
  entityId: string;
  tagIds: string[];
};

/**
 * Set tags for a specific entity (replaces all existing tags)
 */
export function useSetEntityTagsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetEntityTagsInput) => {
      const res = await fetch(`${API_URL}/org/tags/entity`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to set entity tags');
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['entity-tags', variables.entityType, variables.entityId] });
    },
  });
}
